// Persistent game state + versioned (de)serialization.
//
// Save format changes MUST add a migration below instead of editing old ones:
// players' saves from every past version have to keep loading.
import { type Decimal, fromSave, toSave, ZERO } from '../core/decimal';
import type { SkillId } from '../data/skills';
import { RARITIES, SETS, SLOTS } from '../data/equipment';
import { emptyEquipment, type EquipmentState, type Item } from './equipment';
import { PET_BY_ID } from '../data/pets';
import { emptyPets, type PetState } from './pets';
import { emptyMeta, type MetaState } from './meta';

export interface SkillState {
  readyAt: number;     // epoch ms when the cooldown ends
  activeUntil: number; // epoch ms when the effect ends
}

export interface GameState {
  // --- current run (reset on prestige) ---
  gold: Decimal;
  tapLevel: number;
  heroLevels: Record<string, number>;
  /** Active skill levels, bought with gold; reset on ascension. */
  skillLevels: Partial<Record<SkillId, number>>;
  stage: number;
  kills: number;
  farming: boolean;
  /** Enemies' worth of coins bought with purchased Gems this run: boosts this run's Rewind Stardust. */
  runBoughtKills: number;

  // --- persists across prestige ---
  relics: Decimal;
  /** Artifact id -> level (present = discovered). */
  artifacts: Record<string, number>;
  /** Guardian id -> Guardian Stars earned (permanent damage multipliers, BALANCE.stars). */
  heroStars: Record<string, number>;
  /** Skill tree node id -> level. */
  skillTree: Record<string, number>;
  equipment: EquipmentState;
  pets: PetState;
  /** Gems (internal name: Power Cells), earned in play and bought with real money. */
  cells: number;
  /** How many of `cells` were bought with real money (spent first). Only these feed the Rewind bonus. */
  paidCells: number;
  /** Phase 2 retention systems (daily, directives, milestones, drops, boosts). */
  meta: MetaState;
  bestStage: number;
  ascensions: number;
  skills: Partial<Record<SkillId, SkillState>>;
  mana: number;
  settings: { muted: boolean };
  lifetime: {
    taps: number; kills: number; bossKills: number; playSec: number; createdAt: number;
    drops: number; directives: number; casts: number;
  };
  lastSeen: number;
  /** One-time UI hints already shown (e.g. 'relicsIntro'). */
  flags: Record<string, boolean>;
  /** Real-money order ids already credited: a grant replayed by the server is never credited twice. */
  purchases: string[];
}

export function freshRun(): Pick<GameState, 'gold' | 'tapLevel' | 'heroLevels' | 'skillLevels' | 'stage' | 'kills' | 'farming' | 'runBoughtKills'> {
  return { gold: ZERO, tapLevel: 0, heroLevels: {}, skillLevels: {}, stage: 1, kills: 0, farming: false, runBoughtKills: 0 };
}

export function newState(now: number): GameState {
  return {
    ...freshRun(),
    relics: ZERO,
    artifacts: {},
    heroStars: {},
    skillTree: {},
    equipment: emptyEquipment(),
    pets: emptyPets(),
    cells: 0,
    paidCells: 0,
    meta: emptyMeta(),
    bestStage: 1,
    ascensions: 0,
    skills: {},
    mana: 100,
    settings: { muted: false },
    lifetime: { taps: 0, kills: 0, bossKills: 0, playSec: 0, createdAt: now, drops: 0, directives: 0, casts: 0 },
    lastSeen: now,
    flags: {},
    purchases: [],
  };
}

// ---- Serialization -----------------------------------------------------------

export const SAVE_VERSION = 11;

type Raw = Record<string, any>;

/**
 * migrations[n] upgrades a raw save from version n to n + 1.
 * v0 is the original single-file prototype (plain numbers, `muted` at top level).
 */
const migrations: Array<(raw: Raw) => Raw> = [
  (raw) => ({
    ...raw,
    settings: { muted: !!raw.muted },
    lifetime: raw.lifetime ?? { taps: 0, kills: 0, bossKills: 0, playSec: 0, createdAt: raw.lastSeen ?? Date.now() },
    v: 1,
  }),
  // v1 -> v2: artifacts introduced. Relics stop being a passive bonus and become
  // spendable; existing relics carry over untouched so players can spend them.
  (raw) => ({ ...raw, artifacts: {}, v: 2 }),
  // v2 -> v3: skills get levels + mana. Existing players keep every skill they had unlocked
  // at level 1 so nothing they could use disappears.
  (raw) => {
    const unlockedAt: Record<string, number> = { strike: 3, warcry: 8, frenzy: 15, midas: 25 };
    const skillLevels: Record<string, number> = {};
    for (const [id, stage] of Object.entries(unlockedAt)) if ((raw.bestStage ?? 1) >= stage) skillLevels[id] = 1;
    return { ...raw, skillLevels, mana: 100, v: 3 };
  },
  // v3 -> v4: skill tree (points are derived from best stage + ascensions, nothing to convert).
  (raw) => ({ ...raw, skillTree: {}, v: 4 }),
  // v4 -> v5: equipment.
  (raw) => ({ ...raw, equipment: emptyEquipment(), v: 5 }),
  // v5 -> v6: pets.
  (raw) => ({ ...raw, pets: emptyPets(), v: 6 }),
  // v6 -> v7: one-time hint flags.
  (raw) => ({ ...raw, flags: {}, v: 7 }),
  // v7 -> v8: Phase 2 (Power Cells + retention systems).
  (raw) => ({ ...raw, cells: 0, meta: emptyMeta(), v: 8 }),
  // v8 -> v9: real-money Gem purchases (credited order ids).
  (raw) => ({ ...raw, purchases: [], v: 9 }),
  // v9 -> v10: Gems bought with real money tracked apart from earned ones, and the coins bought with
  // them this run (they boost the Rewind's Stardust). Older Gems count as earned.
  (raw) => ({ ...raw, paidCells: 0, runBoughtKills: 0, v: 10 }),
  // v10 -> v11: Guardian Stars.
  (raw) => ({ ...raw, heroStars: {}, v: 11 }),
];

/** Credited order ids kept in the save; only the most recent matter for replay protection. */
const MAX_PURCHASE_IDS = 500;

function sanitizeMeta(raw: any, levels: (o: unknown) => Record<string, number>): MetaState {
  const m = emptyMeta();
  if (!raw || typeof raw !== 'object') return m;
  const n = (x: unknown, d: number) => (typeof x === 'number' && Number.isFinite(x) ? x : d);
  const str = (x: unknown) => (typeof x === 'string' ? x : null);
  m.supplyNextAt = Math.max(0, n(raw.supplyNextAt, 0));
  m.daily = { streak: Math.min(7, Math.max(0, Math.floor(n(raw.daily?.streak, 0)))), lastDay: str(raw.daily?.lastDay) };
  const list = Array.isArray(raw.directives?.list) ? raw.directives.list : [];
  m.directives = {
    day: str(raw.directives?.day),
    list: list.filter((d: any) => d && typeof d.id === 'string')
      .map((d: any) => ({ id: d.id, progress: Math.max(0, n(d.progress, 0)), claimed: !!d.claimed })),
    bonusClaimed: !!raw.directives?.bonusClaimed,
  };
  m.milestones = levels(raw.milestones);
  for (const k of ['scrap2x', 'damage2x'] as const) {
    const until = n(raw.boosts?.[k], 0);
    if (until > 0) m.boosts[k] = until;
  }
  return m;
}

function sanitizePets(raw: any, levels: (o: unknown) => Record<string, number>): PetState {
  const p = emptyPets();
  if (!raw || typeof raw !== 'object') return p;
  const known = (rec: Record<string, number>) => Object.fromEntries(Object.entries(rec).filter(([id]) => PET_BY_ID.has(id)));
  p.levels = known(levels(raw.levels));
  p.food = known(levels(raw.food));
  p.active = typeof raw.active === 'string' && (p.levels[raw.active] ?? 0) > 0 ? raw.active : null;
  p.tapCounter = typeof raw.tapCounter === 'number' && raw.tapCounter >= 0 ? Math.floor(raw.tapCounter) : 0;
  return p;
}

const SLOT_IDS = new Set<string>(SLOTS.map((s) => s.id));
const RARITY_IDS = new Set<string>(RARITIES.map((r) => r.id));
const SET_IDS = new Set<string>(SETS.map((s) => s.id));

/** Keep only well-formed items; drop equipped refs that point at missing items. */
function sanitizeEquipment(raw: any): EquipmentState {
  const eq = emptyEquipment();
  if (!raw || typeof raw !== 'object') return eq;
  const seen = new Set<number>();
  for (const it of Array.isArray(raw.items) ? raw.items : []) {
    if (!it || typeof it.uid !== 'number' || seen.has(it.uid)) continue;
    if (!SLOT_IDS.has(it.slot) || !RARITY_IDS.has(it.rarity) || typeof it.stage !== 'number') continue;
    const item: Item = { uid: it.uid, slot: it.slot, rarity: it.rarity, stage: Math.max(1, it.stage) };
    if (SET_IDS.has(it.set)) item.set = it.set;
    eq.items.push(item);
    seen.add(it.uid);
  }
  for (const [slot, uid] of Object.entries(raw.equipped ?? {})) {
    const item = eq.items.find((i) => i.uid === uid);
    if (item && item.slot === slot) eq.equipped[item.slot] = item.uid;
  }
  eq.nextUid = Math.max(typeof raw.nextUid === 'number' ? raw.nextUid : 1, ...eq.items.map((i) => i.uid + 1), 1);
  return eq;
}

export function serialize(s: GameState): string {
  return JSON.stringify({
    v: SAVE_VERSION,
    ...s,
    gold: toSave(s.gold),
    relics: toSave(s.relics),
  });
}

export function deserialize(json: string, now: number): GameState {
  let raw: Raw = JSON.parse(json);
  if (typeof raw !== 'object' || raw === null) throw new Error('save is not an object');
  let v = typeof raw.v === 'number' ? raw.v : 0;
  if (v > SAVE_VERSION) throw new Error(`save version ${v} is newer than this build (${SAVE_VERSION})`);
  for (; v < SAVE_VERSION; v++) raw = migrations[v]!(raw);

  const base = newState(now);
  const num = (x: unknown, d: number) => (typeof x === 'number' && Number.isFinite(x) ? x : d);
  const levels = (obj: unknown): Record<string, number> => {
    const out: Record<string, number> = {};
    for (const [k, lvl] of Object.entries(typeof obj === 'object' && obj ? obj : {})) {
      const n = num(lvl, 0);
      if (n > 0) out[k] = Math.floor(n);
    }
    return out;
  };
  return {
    gold: fromSave(raw.gold),
    tapLevel: Math.max(0, Math.floor(num(raw.tapLevel, 0))),
    heroLevels: levels(raw.heroLevels),
    skillLevels: levels(raw.skillLevels),
    stage: Math.max(1, Math.floor(num(raw.stage, 1))),
    kills: Math.max(0, Math.floor(num(raw.kills, 0))),
    farming: !!raw.farming,
    runBoughtKills: Math.max(0, num(raw.runBoughtKills, 0)),
    relics: fromSave(raw.relics),
    artifacts: levels(raw.artifacts),
    heroStars: levels(raw.heroStars),
    skillTree: levels(raw.skillTree),
    equipment: sanitizeEquipment(raw.equipment),
    pets: sanitizePets(raw.pets, levels),
    cells: Math.max(0, Math.floor(num(raw.cells, 0))),
    paidCells: Math.min(Math.max(0, Math.floor(num(raw.cells, 0))), Math.max(0, Math.floor(num(raw.paidCells, 0)))),
    meta: sanitizeMeta(raw.meta, levels),
    bestStage: Math.max(1, Math.floor(num(raw.bestStage, 1))),
    ascensions: num(raw.ascensions, 0),
    skills: typeof raw.skills === 'object' && raw.skills ? raw.skills : {},
    mana: Math.max(0, num(raw.mana, 100)),
    settings: { ...base.settings, ...(raw.settings ?? {}) },
    lifetime: { ...base.lifetime, ...(raw.lifetime ?? {}) },
    lastSeen: num(raw.lastSeen, now),
    flags: Object.fromEntries(Object.entries(typeof raw.flags === 'object' && raw.flags ? raw.flags : {})
      .filter(([, v]) => v === true)) as Record<string, boolean>,
    purchases: (Array.isArray(raw.purchases) ? raw.purchases : [])
      .filter((id: unknown): id is string => typeof id === 'string').slice(-MAX_PURCHASE_IDS),
  };
}

